import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { useLocation } from "react-router-dom";
import ThemeButton from "@/components/ThemeButton";
import PaletteFooter from "@/components/PaletteFooter";

import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AppLayout from "@/components/AppLayout";
import DashboardPage from "@/pages/DashboardPage";
import PatientsPage from "@/pages/PatientsPage";

// Marketing site (public)
import MarketingLayout from "@/pages/marketing/MarketingLayout";
import HomePage from "@/pages/marketing/HomePage";
import FeaturesPage from "@/pages/marketing/FeaturesPage";
import HowItWorksPage from "@/pages/marketing/HowItWorksPage";
import VideosPage from "@/pages/marketing/VideosPage";
import VideoGalleryPage from "@/pages/marketing/VideoGalleryPage";
import PricingPage from "@/pages/marketing/PricingPage";
import SecurityPage from "@/pages/marketing/SecurityPage";
import FAQPage from "@/pages/marketing/FAQPage";
import ContactPage from "@/pages/marketing/ContactPage";
import DemoPage from "@/pages/marketing/DemoPage";
import "@/styles/marketing.css";
import PatientDetailPage from "@/pages/PatientDetailPage";
import RegimensPage from "@/pages/RegimensPage";
import RegimenDetailPage from "@/pages/RegimenDetailPage";
import RegimenCreatePage from "@/pages/RegimenCreatePage";
import CalendarCreatePage from "@/pages/CalendarCreatePage";
import CalendarViewPage from "@/pages/CalendarViewPage";
import DrugsPage from "@/pages/DrugsPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import ProvidersPage from "@/pages/ProvidersPage";
import ProfilePage from "@/pages/ProfilePage";
import HelpPage from "@/pages/HelpPage";
import DisclaimerModal from "@/components/DisclaimerModal";

import PatientShell from "@/components/PatientShell";
import MobileHome from "@/pages/mobile/MobileHome";
import MobileCalendar from "@/pages/mobile/MobileCalendar";
import MobileEventDetail from "@/pages/mobile/MobileEventDetail";
import MobileEffects from "@/pages/mobile/MobileEffects";
import MobileMessages from "@/pages/mobile/MobileMessages";
import MobileProfile from "@/pages/mobile/MobileProfile";
import MobileAppointments from "@/pages/mobile/MobileAppointments";
import MobileMedication from "@/pages/mobile/MobileMedication";
import MobileNotifications from "@/pages/mobile/MobileNotifications";
import MobileSettings from "@/pages/mobile/MobileSettings";
import ProviderRequestsPage from "@/pages/ProviderRequestsPage";
import ProviderMessagesPage from "@/pages/ProviderMessagesPage";

function RequireProvider({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "patient") return <Navigate to="/m" replace />;
  return children;
}

function RequirePatient({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "patient") return <Navigate to="/dashboard" replace />;
  return children;
}

/**
 * Smart root: if user is already signed in, send them to the appropriate
 * dashboard. If they're a visitor, render the marketing landing page. This
 * lets the public marketing site live at `/` without breaking the existing
 * post-auth experience.
 */
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={user.role === "patient" ? "/m" : "/dashboard"} replace />;
  return <HomePage />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public marketing site — wraps all unauthenticated pages with the
          MarketingLayout (top nav + footer). The root path uses RootRoute
          so signed-in users are auto-redirected to their dashboard. */}
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<RootRoute />} />
        <Route path="/features"      element={<FeaturesPage />} />
        <Route path="/how-it-works"  element={<HowItWorksPage />} />
        <Route path="/videos"        element={<VideosPage />} />
        <Route path="/videos/all"    element={<VideoGalleryPage />} />
        <Route path="/pricing"       element={<PricingPage />} />
        <Route path="/security"      element={<SecurityPage />} />
        <Route path="/faq"           element={<FAQPage />} />
        <Route path="/contact"       element={<ContactPage />} />
        <Route path="/demo"          element={<DemoPage />} />
      </Route>

      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Patient PWA */}
      <Route path="/m" element={<RequirePatient><PatientShell /></RequirePatient>}>
        <Route index element={<MobileHome />} />
        <Route path="appointments" element={<MobileAppointments />} />
        <Route path="medication" element={<MobileMedication />} />
        <Route path="calendar" element={<MobileCalendar />} />
        <Route path="event/:id" element={<MobileEventDetail />} />
        <Route path="effects" element={<MobileEffects />} />
        <Route path="messages" element={<MobileMessages />} />
        <Route path="notifications" element={<MobileNotifications />} />
        <Route path="settings" element={<MobileSettings />} />
        <Route path="profile" element={<MobileProfile />} />
      </Route>

      {/* Provider/Admin Web — mounted at /dashboard now that / is marketing.
          All previously-at-root paths (/patients, /regimens, etc.) keep their
          URLs so existing links / bookmarks continue to work. */}
      <Route path="/dashboard" element={<RequireProvider><AppLayout /></RequireProvider>}>
        <Route index element={<DashboardPage />} />
      </Route>
      <Route element={<RequireProvider><AppLayout /></RequireProvider>}>
        <Route path="/patients"           element={<PatientsPage />} />
        <Route path="/patients/:id"       element={<PatientDetailPage />} />
        <Route path="/regimens"           element={<RegimensPage />} />
        <Route path="/regimens/new"       element={<RegimenCreatePage />} />
        <Route path="/regimens/:id/edit"  element={<RegimenCreatePage />} />
        <Route path="/regimens/:id"       element={<RegimenDetailPage />} />
        <Route path="/calendars/new"      element={<CalendarCreatePage />} />
        <Route path="/calendars/:id"      element={<CalendarViewPage />} />
        <Route path="/drugs"              element={<DrugsPage />} />
        <Route path="/approvals"          element={<ApprovalsPage />} />
        <Route path="/providers"          element={<ProvidersPage />} />
        <Route path="/requests"           element={<ProviderRequestsPage />} />
        <Route path="/messages"           element={<ProviderMessagesPage />} />
        <Route path="/profile"            element={<ProfilePage />} />
        <Route path="/help"               element={<HelpPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function GlobalChrome() {
  // Hide the floating Theme button + palette footer on:
  //   • The patient PWA (/m/*) — has its own theme controls in Settings.
  //   • The public marketing site — has its own intentional dark cinematic look.
  const { pathname } = useLocation();
  const onPatientShell = pathname.startsWith("/m");
  const MARKETING_PATHS = ["/", "/features", "/how-it-works", "/videos", "/videos/all", "/pricing", "/security", "/faq", "/contact", "/demo"];
  const onMarketing = MARKETING_PATHS.includes(pathname);
  if (onPatientShell || onMarketing) return <DisclaimerModal />;
  return (
    <>
      <DisclaimerModal />
      <ThemeButton floating />
      <PaletteFooter />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <GlobalChrome />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
